/**
 * A control with a staged change on it: outlined, with the committed value
 * struck through directly beneath.
 *
 * Both marks belong to the control, which is why this wraps rather than sitting
 * beside it. An earlier version was a sibling that drew its own bordered box —
 * cheaper to add, and not what the prototype shows: there the control itself is
 * outlined and the old value hangs under it with no chrome of its own.
 *
 * Carries no accept or reject. Those are the ROW's, at the end of it — see
 * `StagedRowActions`. A tick beside one field of a multi-field entry implied a
 * narrower gesture than it performed, and the prototype resolves that by only
 * ever offering one pair per row.
 *
 * Renders its child untouched when nothing is staged at these paths.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { useDraft } from '../DraftContext';
import type { DescribedRow } from '../describedRows';
import type { FieldPath } from '../types';
import { stagedControlSx, stagedWasSx } from './stagedStyles';

export interface StagedControlProps {
  /** Where this control's changes live. A list: one cell can own more than one. */
  readonly paths: readonly FieldPath[];
  /**
   * `was $3,000,000` rather than a bare `$3,000,000`.
   *
   * On in the globals header, where the struck value sits under a labelled
   * field with nothing else around it; off in the coverage grid, where the
   * column heading above already says what the number is and every row would
   * otherwise repeat the word.
   */
  readonly prefixed?: boolean;
  readonly children: React.ReactNode;
}

const StagedControl = ({ paths, prefixed, children }: StagedControlProps) => {
  const { stagedFields } = useDraft();
  const changes = stagedFields.at(paths);

  if (changes.length === 0) {
    return <>{children}</>;
  }

  return (
    <Box sx={stagedControlSx}>
      {children}
      {changes.map((change) => (
        <WasValue key={change.id} change={change} prefixed={prefixed} />
      ))}
    </Box>
  );
};

const WasValue = ({
  change,
  prefixed,
}: {
  readonly change: DescribedRow;
  readonly prefixed?: boolean;
}) => (
  // Centred here rather than in `stagedWasSx`: that style is `display: block`
  // (so left-aligned), and `StagedWas` uses it where the alignment differs —
  // under the billing toggle it is right-aligned to the switch.
  <Typography
    variant="caption"
    color="text.disabled"
    sx={{ ...stagedWasSx, textAlign: 'center' }}
  >
    {prefixed ? 'was ' : ''}
    {change.fromLabel}
  </Typography>
);

export default StagedControl;
